import React from 'react';
import { styled, alpha } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Menu, { MenuProps } from '@mui/material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Icon, MenuItem } from '@mui/material';

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right'
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right'
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0'
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5)
      },
      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity)
      }
    }
  }
}));

interface IProps {
  onSelect: (option: { dateFrom: Date | null; dateTo: Date | null; name?: string }) => void;
  allowFuture?: boolean;
}

const DatesOptionsSelect: React.FC<IProps> = ({ onSelect, allowFuture }) => {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (option: { dateFrom: Date | null; dateTo: Date | null }) => {
    setAnchorEl(null);
    onSelect(option);
  };

  const currentYear = dayjs().year();
  const previousYear = currentYear - 1;

  const todayEnd = dayjs().endOf('day').toDate();
  const safeDate = (to: Date) => (allowFuture ? to : to > todayEnd ? todayEnd : to);

  const dateOptions = [
    {
      name: t('f-date-filter-today'),
      dateFrom: dayjs().startOf('day').toDate(),
      dateTo: safeDate(dayjs().endOf('day').toDate())
    },
    {
      name: t('f-date-filter-yesterday'),
      dateFrom: dayjs().subtract(1, 'day').startOf('day').toDate(),
      dateTo: safeDate(dayjs().subtract(1, 'day').endOf('day').toDate())
    },
    {
      name: t('f-date-filter-this-week'),
      dateFrom: dayjs().startOf('week').toDate(),
      dateTo: safeDate(dayjs().endOf('week').toDate())
    },
    {
      name: t('f-date-filter-last-week'),
      dateFrom: dayjs().subtract(1, 'week').startOf('week').toDate(),
      dateTo: safeDate(dayjs().subtract(1, 'week').endOf('week').toDate())
    },
    {
      name: t('f-date-filter-last-2-weeks'),
      dateFrom: dayjs().subtract(2, 'week').startOf('week').toDate(),
      dateTo: safeDate(dayjs().endOf('week').toDate())
    },
    {
      name: t('f-date-filter-last-14-days'),
      dateFrom: dayjs().subtract(14, 'day').startOf('day').toDate(),
      dateTo: safeDate(dayjs().endOf('day').toDate())
    },
    {
      name: t('f-date-filter-this-month'),
      dateFrom: dayjs().startOf('month').toDate(),
      dateTo: safeDate(dayjs().endOf('month').toDate())
    },
    {
      name: t('f-date-filter-last-month'),
      dateFrom: dayjs().subtract(1, 'month').startOf('month').toDate(),
      dateTo: safeDate(dayjs().subtract(1, 'month').endOf('month').toDate())
    },
    {
      name: t('f-date-filter-last-30-days'),
      dateFrom: dayjs().subtract(30, 'day').startOf('day').toDate(),
      dateTo: safeDate(dayjs().endOf('day').toDate())
    },
    {
      name: `${currentYear}`,
      dateFrom: dayjs(`${currentYear}-01-01`).startOf('year').toDate(),
      dateTo: safeDate(dayjs(`${currentYear}-12-31`).endOf('year').toDate())
    },
    {
      name: `${previousYear}`,
      dateFrom: dayjs(`${previousYear}-01-01`).startOf('year').toDate(),
      dateTo: safeDate(dayjs(`${previousYear}-12-31`).endOf('year').toDate())
    },
    {
      name: t('f-date-filter-last-2-years'),
      dateFrom: dayjs().subtract(2, 'year').startOf('year').toDate(),
      dateTo: safeDate(dayjs().endOf('year').toDate())
    },
    {
      name: t('f-date-filter-last-5-years'),
      dateFrom: dayjs().subtract(5, 'year').startOf('year').toDate(),
      dateTo: safeDate(dayjs().endOf('year').toDate())
    },
    {
      name: t('f-date-filter-last-10-years'),
      dateFrom: dayjs().subtract(10, 'year').startOf('year').toDate(),
      dateTo: safeDate(dayjs().endOf('year').toDate())
    }
  ];

  return (
    <div>
      <Button
        id="demo-customized-button"
        aria-controls={open ? 'demo-customized-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        sx={{ width: '100%', height: 40 }}
        variant="contained"
        color="inherit"
        startIcon={<Icon>{'event_available'}</Icon>}
        disableElevation
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
      >
        {t('f-date-filter-dates')}
      </Button>
      <StyledMenu
        id="demo-customized-menu"
        slotProps={{
          list: { 'aria-labelledby': 'demo-customized-button' }
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
      >
        {dateOptions.map((option) => (
          <MenuItem key={option.name} onClick={() => handleClose(option)} disableRipple>
            {option.name}
          </MenuItem>
        ))}
      </StyledMenu>
    </div>
  );
};

export default DatesOptionsSelect;
